import type { PromoUploadStatusType } from '@entities/adCampaign';
import { getMediaPreviewKind } from 'application/utils/MediaPreviewUtils';
import FileUtils from 'application/utils/FileUtils';

export const DEFAULT_VIDEO_PROMO_IMAGE_DURATION = 10000;
export const VIDEO_PROMO_CLOCK_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const DURATION_PATTERN = /^(\d{2}):([0-5]\d):([0-5]\d)$/;

export const getVideoPromoKind = (item: PromoUploadStatusType) =>
  getMediaPreviewKind({
    type: item.type,
    extension: FileUtils.getOriginalMediafileSource(item.sourcefiles)?.extension ?? item.title.split('.').pop(),
  });

export const parseVideoPromoDuration = (raw: string): number | undefined => {
  const match = DURATION_PATTERN.exec(raw);
  if (!match) return undefined;
  const duration = (Number(match[1]) * 3600 + Number(match[2]) * 60 + Number(match[3])) * 1000;
  return duration >= 1000 ? duration : undefined;
};

export const formatVideoPromoDuration = (duration: number) =>
  [Math.floor(duration / 3600000), Math.floor(duration / 60000) % 60, Math.floor(duration / 1000) % 60]
    .map((part) => String(part).padStart(2, '0'))
    .join(':');

export const getVideoPromoDuration = (item: PromoUploadStatusType) => {
  if (!item.promoAdId) return item.playDuration ?? DEFAULT_VIDEO_PROMO_IMAGE_DURATION;
  return item.playDuration ?? item.duration ?? DEFAULT_VIDEO_PROMO_IMAGE_DURATION;
};

export const getVideoPromoDurationRaw = (item: PromoUploadStatusType, durationById: Record<string, string>) =>
  durationById[item.id ?? ''] ?? formatVideoPromoDuration(getVideoPromoDuration(item));

export const areVideoPromosValid = (
  items: PromoUploadStatusType[],
  isExactTime: boolean,
  timeById: Record<string, string>,
  durationById: Record<string, string>
) =>
  items.length > 0 &&
  items.every(
    (item) =>
      !item.isLoading &&
      !item.error &&
      !!item.promoId &&
      (getVideoPromoKind(item) !== 'image' ||
        parseVideoPromoDuration(getVideoPromoDurationRaw(item, durationById)) !== undefined) &&
      (!isExactTime || VIDEO_PROMO_CLOCK_PATTERN.test(timeById[item.id ?? ''] ?? ''))
  );

export const projectVideoPromoDraft = (
  items: PromoUploadStatusType[],
  isExactTime: boolean,
  timeById: Record<string, string>,
  durationById: Record<string, string>
) =>
  items.map((item) => {
    if (!item.promoId) throw new Error('Cannot serialize a promo without a resource');
    return {
      ...item,
      promoId: item.promoId,
      time: isExactTime ? timeById[item.id ?? ''] : item.time,
      playDuration:
        getVideoPromoKind(item) === 'image'
          ? parseVideoPromoDuration(getVideoPromoDurationRaw(item, durationById))
          : item.playDuration,
    };
  });
